import { useEffect, useRef, useState } from "react";
import {
  CATEGORIES,
  WHEN_OPTIONS,
  A11Y_OPTIONS,
} from "../utils/searchFilters";
import "./SearchFilters.css";

// Categories shown before the « + N autres » disclosure.
const VISIBLE_CATEGORIES = 8;

function toggleInList(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function csvOrNull(list) {
  return list.length ? list.join(",") : null;
}

function FilterRow({ label, children, sublabel, after }) {
  return (
    <div className="pa-filter-row">
      <span className="pa-label pa-filter-row__label">{label}</span>
      {children}
      {sublabel && (
        <>
          <span className="pa-sep" aria-hidden="true" />
          <span className="pa-label pa-filter-row__sublabel">{sublabel}</span>
          {after}
        </>
      )}
    </div>
  );
}

export function SearchFilters({
  filters,
  onChange,
  onReset,
  layout = "band",
  idPrefix = "filters",
}) {
  const [dateOpen, setDateOpen] = useState(Boolean(filters.date));
  const [showAllCats, setShowAllCats] = useState(false);
  const dateRef = useRef(null);
  const wantDateFocus = useRef(false);

  // Focus (and, where allowed, open) the date field right after it appears.
  useEffect(() => {
    if (!wantDateFocus.current) return;
    wantDateFocus.current = false;
    const el = dateRef.current;
    if (!el) return;
    el.focus();
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
      } catch {
        // Some browsers only allow showPicker inside a user gesture.
      }
    }
  }, [dateOpen]);

  const clearWhen = () => {
    setDateOpen(false);
    onChange({ when: null, date: null });
  };

  const setWhen = (key) => {
    setDateOpen(false);
    onChange({ when: key, date: null });
  };

  const openDate = () => {
    wantDateFocus.current = true;
    setDateOpen(true);
  };

  const setDate = (value) => {
    onChange({ when: null, date: value || null });
  };

  const toggleCat = (key) => {
    onChange({ cats: csvOrNull(toggleInList(filters.cats, key)) });
  };

  const setPrice = (value) => {
    onChange({ price: value || null });
  };

  const toggleAccess = (key) => {
    onChange({ access: csvOrNull(toggleInList(filters.access, key)) });
  };

  const whenIsAny = !filters.when && !filters.date;
  const hasHiddenSelection = CATEGORIES.slice(VISIBLE_CATEGORIES).some((c) =>
    filters.cats.includes(c.key)
  );
  const catsExpanded = showAllCats || hasHiddenSelection;
  const visibleCats = catsExpanded
    ? CATEGORIES
    : CATEGORIES.slice(0, VISIBLE_CATEGORIES);
  const hiddenCount = CATEGORIES.length - VISIBLE_CATEGORIES;

  const priceSegment = (
    <div className="pa-seg" role="group" aria-label="Tarif">
      <button
        type="button"
        className="pa-seg__item"
        aria-pressed={!filters.price}
        onClick={() => setPrice(null)}
      >
        Tous
      </button>
      <button
        type="button"
        className="pa-seg__item"
        aria-pressed={filters.price === "free"}
        onClick={() => setPrice("free")}
      >
        Gratuit
      </button>
      <button
        type="button"
        className="pa-seg__item"
        aria-pressed={filters.price === "paid"}
        onClick={() => setPrice("paid")}
      >
        Payant
      </button>
    </div>
  );

  const accessChips = (
    <div className="pa-chip-row">
      {A11Y_OPTIONS.map((a) => (
        <button
          key={a.key}
          type="button"
          className="pa-chip"
          aria-pressed={filters.access.includes(a.key)}
          onClick={() => toggleAccess(a.key)}
        >
          {a.key === "pmr" && (
            <i className="bi bi-person-wheelchair" aria-hidden="true"></i>
          )}
          {a.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className={layout === "band" ? "pa-band" : "pa-filters-stack"}>
      <FilterRow label="Quand">
        <div className="pa-chip-row">
          <button
            type="button"
            className="pa-chip pa-chip--mono"
            aria-pressed={whenIsAny}
            onClick={clearWhen}
          >
            N'importe quand
          </button>
          {WHEN_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className="pa-chip pa-chip--mono"
              aria-pressed={filters.when === opt.key}
              onClick={() => setWhen(opt.key)}
            >
              {opt.label}
            </button>
          ))}
          <button
            type="button"
            className="pa-chip pa-chip--mono"
            aria-pressed={Boolean(filters.date)}
            onClick={openDate}
          >
            <i className="bi bi-calendar3" aria-hidden="true"></i>
            Date précise
          </button>
          {(dateOpen || filters.date) && (
            <>
              <label className="visually-hidden" htmlFor={`${idPrefix}-date`}>
                Choisir une date précise
              </label>
              <input
                ref={dateRef}
                id={`${idPrefix}-date`}
                type="date"
                className="pa-date pa-date--inline"
                value={filters.date || ""}
                onChange={(e) => setDate(e.target.value)}
              />
            </>
          )}
        </div>
      </FilterRow>

      <FilterRow label="Type">
        <div className="pa-chip-row">
          {visibleCats.map((c) => {
            const on = filters.cats.includes(c.key);
            return (
              <button
                key={c.key}
                type="button"
                className="pa-chip"
                aria-pressed={on}
                onClick={() => toggleCat(c.key)}
              >
                {c.label}
                {on && (
                  <i className="bi bi-x-lg pa-chip__x" aria-hidden="true"></i>
                )}
              </button>
            );
          })}
          {!catsExpanded && hiddenCount > 0 && (
            <button
              type="button"
              className="pa-chip-more"
              onClick={() => setShowAllCats(true)}
            >
              + {hiddenCount} autres
            </button>
          )}
        </div>
      </FilterRow>

      {layout === "band" ? (
        <FilterRow label="Tarif" sublabel="Accès" after={accessChips}>
          {priceSegment}
        </FilterRow>
      ) : (
        <>
          <FilterRow label="Tarif">{priceSegment}</FilterRow>
          <FilterRow label="Accès">{accessChips}</FilterRow>
        </>
      )}

      {layout !== "band" && (
        <button
          type="button"
          className="pa-btn pa-btn--quiet pa-btn--sm pa-btn--block pa-filters-stack__reset"
          onClick={onReset}
        >
          <i className="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
          Réinitialiser les filtres
        </button>
      )}
    </div>
  );
}

export function FilterDrawer({ open, onClose, children, activeCount = 0 }) {
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <>
      <div
        className={`filter-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`filter-drawer ${open ? "open" : ""}`}
        aria-label="Filtres de recherche"
        aria-hidden={!open}
      >
        <div className="filter-drawer-header">
          <h2 className="h3 mb-0">Filtres</h2>
          <button
            type="button"
            className="pa-icon-btn"
            aria-label="Fermer les filtres"
            onClick={onClose}
          >
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>
        <div className="filter-drawer-body">{children}</div>
        <div className="filter-drawer-footer">
          <button
            type="button"
            className="pa-btn pa-btn--primary pa-btn--block"
            onClick={onClose}
          >
            Voir les résultats
            {activeCount > 0 && ` (${activeCount} filtre${activeCount > 1 ? "s" : ""})`}
          </button>
        </div>
      </aside>
    </>
  );
}
